import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  AuthUser,
  clearSession,
  getStoredToken,
  getStoredUser,
  SESSION_EXPIRED_EVENT,
  setSession,
} from '../services/api';

type AuthContextValue = {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  needsCompanySetup: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  patchUser: (partial: Partial<AuthUser>) => void;
  establishSession: (accessToken: string, nextUser: AuthUser) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [token, setToken] = useState<string | null>(() => getStoredToken());

  const login = useCallback(async (email: string, password: string) => {
    const result = await api.login(email, password);
    setSession(result.accessToken, result.user);
    setToken(result.accessToken);
    setUser(result.user);
    return result.user;
  }, []);

  const logout = useCallback(() => {
    clearSession();
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.getMe();
    const currentToken = getStoredToken();
    if (currentToken) {
      setSession(currentToken, me);
    }
    setUser(me);
  }, []);

  const patchUser = useCallback((partial: Partial<AuthUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...partial };
      const currentToken = getStoredToken();
      if (currentToken) {
        setSession(currentToken, next);
      }
      return next;
    });
  }, []);

  const establishSession = useCallback(
    (accessToken: string, nextUser: AuthUser) => {
      setSession(accessToken, nextUser);
      setToken(accessToken);
      setUser(nextUser);
    },
    [],
  );

  useEffect(() => {
    const onSessionExpired = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    void api
      .getMe()
      .then((me) => {
        setSession(token, me);
        setUser(me);
      })
      .catch(() => {
        // keep stored session until next 401
      });
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: Boolean(token && user),
      isAdmin: user?.role === 'ADMIN',
      isSuperAdmin: user?.role === 'SUPER_ADMIN',
      needsCompanySetup:
        user?.role === 'ADMIN' && user.tenantSetupCompleted === false,
      login,
      logout,
      refreshUser,
      patchUser,
      establishSession,
    }),
    [user, token, login, logout, refreshUser, patchUser, establishSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
